import { useEffect, useMemo, useState } from 'react';
import { useTemplate } from '../../../../../features/template/TemplateProvider';
import { ButtonCancel, ButtonPrimary } from '../../../../../utils/Button';
import FocusControl from '../../../../../utils/FocusControl';
import { InputNumberField, SelectOption, addOption } from '../../../../../utils/InputField';
import { ImageUploadingIcon } from '../../../../../utils/SVGIcon';
import cn from '../../../../../utils/cn';
import { ApiUpdateServicePackagePricing } from '../../../Services/ServiceOperation';
import { ApiGetAllServicesPackage } from '../../ProductOperation';

function UpdatePackageServiceModal({ closeModal, handleReload, modalData, packageInfo }) {
    const [formData, setFormData] = useState({
        ApplicationPackageId: packageInfo?.id || 0,
        AppServiceId: modalData.appServiceId || 0,
        PackageId: modalData?.appServicePackageId || 0,
        Id: modalData?.id || 0,
        Currency: modalData?.currency || '',
        Amount: modalData?.amount === -102 ? 'Free' : modalData?.amount || 0,
        VatPercent: modalData?.vatPercent || 0,
        Status: modalData?.status || ''
    });
    const [serviceData, setServiceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [isError, setIsError] = useState('');
    const [isFree, setIsFree] = useState(modalData?.amount === -102);
    const { setMessage } = useTemplate();
    // -- Load Service Data
    useEffect(() => {
        // Fetch Service Data
        setLoading(true);
        ApiGetAllServicesPackage(packageInfo.role)
            .then(({ data: resData }) => {
                setLoading(false);
                setServiceData(resData.services);
            })
            .catch((error) => {
                setLoading(false);
                setMessage({ type: 'error', text: error.toString(0) });
            });
    }, [packageInfo.role, setMessage]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };
    const handleFree = (e) => {
        if (e.target.checked) {
            setIsFree(true);
            setFormData((prevData) => ({ ...prevData, Amount: 'Free', VatPercent: 0 }));
        } else {
            setIsFree(false);
            setFormData((prevData) => ({ ...prevData, Amount: 0 }));
        }
    };

    const payload = useMemo(() => {
        if (isFree) {
            return {
                ...formData,
                Amount: -102,
                VatPercent: 0
            };
        }
        return {
            ...formData
        };
    }, [formData, isFree]);
    const handleUpdatePackPricing = (e) => {
        setBtnLoading(true);
        e.preventDefault();
        ApiUpdateServicePackagePricing(
            payload.AppServiceId,
            payload.PackageId,
            payload.Id,
            payload,
            setIsError
        )
            .then(({ title }) => {
                setBtnLoading(false);
                setMessage({ type: 'success', text: title });
                closeModal();
                handleReload();
            })
            .catch((error) => {
                setBtnLoading(false);
                setMessage({ type: 'error', text: error.toString(0) });
            });
    };

    FocusControl(isError, setIsError, formData);
    return (
        <form onSubmit={handleUpdatePackPricing} className="flex flex-col gap-4 relative">
            {loading && (
                <div className="absolute size-40 top-0 left-1/2 -translate-x-1/2">
                    <ImageUploadingIcon />
                </div>
            )}
            <SelectOption
                label="Package"
                name="PackageId"
                formData={formData}
                onChange={handleChange}
                options={serviceData?.map((item) =>
                    addOption(
                        item?.packageId,
                        `${item.serviceTitle} (${item.packageName})`,
                        item?.packageId
                    )
                )}
                disabled
            />

            <SelectOption
                label="Currency"
                name="Currency"
                formData={formData}
                onChange={handleChange}
                options={['thb', 'usd', 'eur', 'bdt']?.map((item) =>
                    addOption(item, item?.toUpperCase(), item.toUpperCase())
                )}
                disabled
            />
            <div className="flex gap-3">
                <div className={cn('flex flex-col w-full custom-field relative')}>
                    <div className="flex justify-between items-center pl-1 mb-1 text-sm md:text-base capitalize w-full font-normal text-left">
                        <span>
                            Amount <span className="text-error"> *</span>
                        </span>
                        <label className="flex justify-between items-center gap-2">
                            <span>Free</span>
                            <input
                                type="checkbox"
                                name="isFree"
                                checked={isFree}
                                onChange={handleFree}
                                className="mr-1"
                            />
                        </label>
                    </div>
                    <input
                        type="text"
                        name="Amount"
                        value={formData.Amount}
                        onChange={handleChange}
                        placeholder="Amount"
                        readOnly={isFree}
                        autoComplete="off"
                        className={cn(
                            `flex-shrink w-full h-8.5 md:h-10 bg-transparent rounded-lg block outline-none border border-primary-200 px-2 text-primary text-xs md:text-sm leading-6 font-normal read-only:border-primary-100 read-only:bg-primary-50/50 read-only:text-primary-700 read-only:cursor-default`
                        )}
                    />
                </div>
                <InputNumberField
                    label="Vat(%)"
                    name="VatPercent"
                    placeholder="Type Vat(%)..."
                    formData={formData}
                    onChange={handleChange}
                    disabled={isFree}
                />
            </div>
            <SelectOption
                label="Status"
                name="Status"
                formData={formData}
                onChange={handleChange}
                options={['Active', 'Inactive']?.map((item) =>
                    addOption(item, item, item?.toLowerCase())
                )}
            />
            <div className="flex gap-x-2 justify-end">
                <ButtonCancel title="Cancel" onClick={closeModal} />
                <ButtonPrimary
                    title="Update"
                    btnType="submit"
                    loading={btnLoading}
                    disabled={serviceData.length === 0}
                />
            </div>
        </form>
    );
}

export default UpdatePackageServiceModal;
